import { isEqual } from 'lodash'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { PickPointLocation } from 'sections/LKSection/modules/OrderModule/types'
import { Pvz } from 'sections/LKSection/modules/PvzModule/stores/entity/Pvz'
import {
  DEFAULT_END_TIME,
  DEFAULT_GENERAL_TIME,
  DEFAULT_START_TIME,
  DEFAULT_WEEK_DAYS,
  MIN_OPENING_HOURS,
} from 'sections/LKSection/shared/PickPointSharedModule/data'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Timetable, TimetableDays, WeekDays } from 'sections/LKSection/shared/PickPointSharedModule/types'

import { getTimetableModel } from '../../utils'
import { InitialTimetableModelStateType } from './types'

export class TimetableModel {
  @observable timetableStart = DEFAULT_START_TIME
  @observable timetableEnd = DEFAULT_END_TIME
  @observable timetableGeneral = DEFAULT_GENERAL_TIME
  @observable weekDays: WeekDays[] = DEFAULT_WEEK_DAYS
  @observable timetableUpdateReason = ''

  @observable initialized = false

  private initialState: InitialTimetableModelStateType | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isNoChanges() {
    return isEqual(this.initialState, this.hashFormState)
  }

  @computed
  get hashFormState(): InitialTimetableModelStateType {
    return {
      timetableStart: this.timetableStart,
      timetableEnd: this.timetableEnd,
      timetableGeneral: this.timetableGeneral,
      weekDays: this.weekDays,
    }
  }

  @computed
  get invalidOpeningHours() {
    const endHours = Number(this.timetableEnd.split(':')[0])
    const startHours = Number(this.timetableStart.split(':')[0])
    const endMinutes = Number(this.timetableEnd.split(':')[1])
    const startMinutes = Number(this.timetableStart.split(':')[1])

    return (
      endHours - startHours < MIN_OPENING_HOURS ||
      (endHours - startHours === MIN_OPENING_HOURS && endMinutes - startMinutes < 0)
    )
  }

  @computed
  getTimetable(pickPointType: PickPointType, floorLevel: PickPointLocation): Timetable {
    const [start, end] =
      pickPointType === PickPointType.partner && floorLevel !== PickPointLocation.shoppingMall
        ? this.timetableGeneral.split('-')
        : [this.timetableStart, this.timetableEnd]

    const openClose = { open: start, close: end }

    const days: TimetableDays = {}

    for (const day of this.weekDays) {
      days[day.value] = { ...openClose, is_day_off: day.isDayOff }
    }

    return {
      daily: openClose,
      sat: openClose,
      sun: openClose,
      week: {
        days,
      },
    }
  }

  init = (pvz: Pvz) => {
    const timetableModel = getTimetableModel(pvz.timetable)
    const timetable = timetableModel.daily
    const days = timetableModel.week?.days

    const weekDays = this.weekDays.map((day) => {
      const isDayOff = days?.[day.value].is_day_off ?? false
      return { ...day, isDayOff }
    })

    this.setTimetableStart(timetable.open)
    this.setTimetableEnd(timetable.close)
    this.setTimetableGeneral(`${timetable.open}-${timetable.close}`)
    this.setWeekDays(weekDays)

    this.setInitialState(this.hashFormState)
    this.setInitialized(true)
  }

  @action
  setTimetableStart = (value: string) => {
    this.timetableStart = value
  }

  @action
  setTimetableEnd = (value: string) => {
    this.timetableEnd = value
  }

  @action
  setTimetableGeneral = (value: string) => {
    this.timetableGeneral = value
  }

  @action
  setWeekDays = (value: WeekDays[]) => {
    this.weekDays = value
  }

  @action
  setTimetableUpdateReason = (value: string) => {
    this.timetableUpdateReason = value
  }

  @action
  setInitialState = (value: InitialTimetableModelStateType) => {
    this.initialState = value
  }

  @action
  setInitialized(value: boolean) {
    this.initialized = value
  }

  @action
  reset = () => {
    this.setTimetableStart(DEFAULT_START_TIME)
    this.setTimetableEnd(DEFAULT_END_TIME)
    this.setTimetableGeneral(DEFAULT_GENERAL_TIME)
    this.setWeekDays(DEFAULT_WEEK_DAYS)
  }
}
